import { Provider } from "react-redux"
import { Toaster } from "sonner"

import { useDetectGroupTimezone } from "@/features/group/detect-timezone"

import { ErrorBoundary } from "@/shared/ui/error-boundary"
import { TooltipProvider } from "@/shared/ui/tooltip"

import { AppProvider, useAppContext } from "./providers/app-provider"
import { CloneLaunchGate } from "./providers/clone-launch-gate"
import { LanguageProvider } from "./providers/language-provider"
import { ReducedMotionProvider } from "./providers/reduced-motion-provider"
import { ReactRouterProvider } from "./providers/router-provider"
import { store } from "./store"

// Clears the floating bottom nav (68px bar + 12px margin).
const TOAST_OFFSET = { top: "16px", bottom: "96px" }

const GroupTimezoneDetector = () => {
  const { chatId, isInitialized, captchaMode, shareToken } = useAppContext()
  useDetectGroupTimezone({ chatId, isInitialized, captchaMode, shareToken })
  return null
}

const App = () => {
  const handleRefreshPage = () => {
    window.location.reload()
  }

  return (
    <ErrorBoundary
      fallback={
        <div className="flex min-h-screen flex-col items-center justify-center bg-gray-900 p-4 text-gray-100">
          <div className="text-center">
            <h1 className="mb-4 text-2xl font-bold">Application Error</h1>
            <p className="mb-4 text-gray-400">Something went wrong. Please refresh the page to try again.</p>
            <button
              onClick={handleRefreshPage}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
              Refresh Page
            </button>
          </div>
        </div>
      }
      onError={(error, errorInfo) => {
        console.error("Application error:", error, errorInfo)
      }}>
      <Provider store={store}>
        <AppProvider>
          <ReducedMotionProvider>
            <LanguageProvider>
              <TooltipProvider>
                {/* Bottom, above the floating nav: a toast at the top covered the header and switch of the sheet it reported on. */}
                <Toaster
                  theme="dark"
                  position="bottom-center"
                  closeButton
                  richColors
                  duration={4000}
                  visibleToasts={5}
                  offset={TOAST_OFFSET}
                  mobileOffset={TOAST_OFFSET}
                  gap={8}
                />
                <CloneLaunchGate>
                  <GroupTimezoneDetector />
                  <ReactRouterProvider />
                </CloneLaunchGate>
              </TooltipProvider>
            </LanguageProvider>
          </ReducedMotionProvider>
        </AppProvider>
      </Provider>
    </ErrorBoundary>
  )
}

export default App
